import { apiFetch } from "../utils/apiFetch";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { FiArrowRight, FiCheckCircle, FiLoader, FiUsers } from "react-icons/fi";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { loadRazorpay } from "../utils/loadRazorpay";

async function request(path, body) {
  const response = await apiFetch(process.env.API_URL + "/api/" + path, body === undefined ? {} : {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) throw Error(data.message || data.error || "Could not complete this request");
  return data;
}
const formatPrice = pricing => new Intl.NumberFormat(undefined, { style: "currency", currency: pricing.currency }).format(pricing.amount / 100);

export default function Join() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const { userId, userInfo, authLoading } = useAuth();
  const { inviteCode, courseName, inviterName, memberCount, slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [pending, setPending] = useState(Boolean(params.get("purchase")));
  const [purchaseId, setPurchaseId] = useState(params.get("purchase"));
  const [status, setStatus] = useState("created");
  const [check, setCheck] = useState(0);
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  useEffect(() => {
    if (authLoading) return;
    let disposed = false;
    setInfo(null); setError("");
    if (!userId) {
      try { localStorage.setItem("pendingInvitation", (slug ? "/group/" + encodeURIComponent(slug) : "/" + [courseName, inviteCode, inviterName, memberCount].map(encodeURIComponent).join("/")) + (params.toString() ? "?" + params : "")); } catch {}
      if (slug) request("group-directory/" + encodeURIComponent(slug)).then(data => { if (!disposed) setInfo(data); }).catch(error => { if (!disposed) setError(error.message); });
      return () => { disposed = true; };
    }
    request("group-purchases/" + (slug ? "group/" + encodeURIComponent(slug) : "invite/" + encodeURIComponent(inviteCode)))
      .then(data => {
        if (disposed) return;
        setInfo(data);
        if (data.purchase) { setPurchaseId(data.purchase.id); setStatus(data.purchase.paid === false && ["authorized", "captured"].includes(data.purchase.status) ? "created" : data.purchase.status); }
      }).catch(error => { if (!disposed) setError(error.message); });
    return () => { disposed = true; };
  }, [userId, authLoading, inviteCode, slug]);

  const enter = async id => {
    const data = id
      ? await request("group-purchases/" + id + "/join", {})
      : slug ? await request("group-purchases/group/" + encodeURIComponent(slug) + "/join", {}) : await request("workspace/join/" + encodeURIComponent(inviteCode));
    client.invalidateQueries({ queryKey: ["account", userId] });
    if (active.current) navigate("/courses/" + data.workspace.slug);
  };
  useEffect(() => {
    if (!userId || !purchaseId) return;
    let disposed = false, timer;
    const started = Date.now();
    const poll = async () => {
      try {
        const data = await request("group-purchases/" + purchaseId);
        if (disposed) return;
        setStatus(data.status);
        if (data.paid) {
          setWaiting(false); setPending(false);
          await enter(purchaseId);
          return;
        }
        if (data.status === "refunded") {
          setWaiting(false); setPending(false);
          setError("This payment was refunded. Contact the group owner.");
          return;
        }
        if (data.status === "failed" && !waiting) { setPending(false); return; }
      } catch (error) { if (!disposed) setError(error.message); }
      if (disposed) return;
      if (Date.now() - started < 120000) timer = setTimeout(poll, 3000);
      else { setWaiting(false); setPending(true); }
    };
    poll();
    return () => { disposed = true; clearTimeout(timer); };
  }, [userId, purchaseId, check]);

  const join = async () => {
    if (busy || waiting) return;
    setBusy(true); setError(""); setPending(false);
    try {
      if (info?.group.pricing?.access !== "paid" || info?.exempt) { await enter(); return; }
      await loadRazorpay();
      const order = await request("group-purchases/" + (slug ? "group/" + encodeURIComponent(slug) + "/checkout" : "checkout/" + encodeURIComponent(inviteCode)), {});
      setPurchaseId(order.purchaseId);
      setParams({ purchase: order.purchaseId }, { replace: true });
      if (order.paid) { await enter(order.purchaseId); return; }
      let submitted = false;
      const checkout = new window.Razorpay({
        key: order.keyId, subscription_id: order.subscriptionId,
        name: info.group.name, description: "Monthly group membership",
        prefill: { name: userInfo?.username, email: userInfo?.email },
        theme: { color: "#c2410c" },
        handler: async response => {
          submitted = true;
          if (!active.current) return;
          setWaiting(true); setBusy(false); setError("");
          try { await request("group-purchases/" + order.purchaseId + "/verify", response); }
          catch (error) { if (active.current) setError(error.message); }
          if (active.current) setCheck(value => value + 1);
        },
        modal: { ondismiss: () => {
          if (active.current && !submitted) { setBusy(false); setPending(true); setCheck(value => value + 1); }
        } },
      });
      checkout.on("payment.failed", () => {
        if (!active.current) return;
        setPending(true);
        setError("Payment was not completed. Close checkout and check the payment status before trying again.");
      });
      checkout.open();
    } catch (error) { setError(error.message); setBusy(false); }
  };
  const group = info?.group;
  const paid = group?.pricing?.access === "paid" && !info?.exempt;
  return <main className="flex min-h-screen items-center justify-center bg-white px-4 py-10 text-gray-950">
    <section className="w-full max-w-md rounded-lg border border-gray-300 bg-white p-6 shadow-lg sm:p-8">
      {group?.thumbnail && <img src={group.thumbnail} alt="" className="mb-5 aspect-video w-full rounded-md object-cover" />}
      <h1 className="break-words text-2xl font-semibold">{group?.name || courseName || "Join group"}</h1>
      {group?.description && <p className="mt-3 text-sm text-gray-600">{group.description}</p>}
      {(group || memberCount) && <p className="mt-4 flex items-center gap-2 text-sm text-gray-600"><FiUsers aria-hidden="true" />{group?.memberCount ?? memberCount} members</p>}
      {paid && <><p className="mt-6 text-xl font-semibold">{formatPrice(group.pricing)} <span className="text-sm font-normal text-gray-500">/ month</span></p><p className="mt-2 text-xs leading-5 text-gray-600">Renews monthly for up to 120 payments. Cancel renewal in group Settings.</p></>}
      {error && <p role="alert" className="mt-5 text-sm text-red-700">{error}</p>}
      {authLoading ? <FiLoader aria-label="Checking session" className="mx-auto mt-6 animate-spin" /> : !userId ? <>
        <p className="mt-6 text-sm text-gray-600">Sign in to join this group.</p>
        <Link to="/login" className="mt-4 flex h-11 items-center justify-center rounded-md bg-orange-700 font-semibold text-white">Sign In</Link>
      </> : waiting ? <div role="status" className="flex min-h-32 flex-col items-center justify-center gap-3">
        <FiLoader className="h-7 w-7 animate-spin text-orange-700" /><p className="text-sm font-medium">Verifying payment</p>
      </div> : ["authorized", "captured"].includes(status) ? <button onClick={() => enter(purchaseId).catch(error => setError(error.message))} className="mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-md bg-green-700 font-semibold text-white"><FiCheckCircle />Open group</button>
      : status === "refunded" ? null : <>
        {pending && <p role="status" className="mt-5 text-sm text-gray-600">Payment confirmation is pending. Check its status before paying again.</p>}
        {pending ? <div className="mt-4 space-y-3">
          <button onClick={() => { setWaiting(true); setPending(false); setCheck(value => value + 1); }} className="h-11 w-full rounded-md border border-gray-300 font-semibold">Check payment status</button>
          {status === "created" && info && <button disabled={busy} onClick={join} className="h-11 w-full rounded-md bg-orange-700 font-semibold text-white disabled:opacity-60">Resume checkout</button>}
        </div>
          : <button disabled={busy || !info} aria-busy={busy} onClick={join} className="mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-md bg-orange-700 px-4 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60">
            {busy ? <FiLoader aria-label="Preparing checkout" className="animate-spin" /> : <>{paid ? "Pay and join" : "Join group"}<FiArrowRight /></>}
          </button>}
        {status === "failed" && !waiting && <p className="mt-3 text-sm text-gray-600">The last payment attempt failed.</p>}
      </>}
      <Link to="/explore" className="mt-5 block text-center text-sm font-medium text-gray-600">Back to Explore</Link>
    </section>
  </main>;
}
